import React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Badge,
  alpha,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Hotel as HotelIcon,
  Settings as SettingsIcon,
  Chat as ChatIcon,
  AddBusiness as AddHotelIcon,
  CameraAlt as OCRIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '@features/auth/context/AuthContext';

// Logo element at the top of sidebar
const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2),
  marginBottom: theme.spacing(1),
  margin: theme.spacing(1, 1, 1, 1),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: alpha('#ffffff', 0.7),
  padding: theme.spacing(1.5, 2, 0.5, 2),
  marginTop: theme.spacing(1),
}));

interface NavigationItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number | string | null;
  children?: NavigationItem[];
  isNew?: boolean;
  isPro?: boolean;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

interface SidebarNavigationProps {
  open: boolean;
  onNavigate: (path: string) => void;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({ open, onNavigate }) => {
  const theme = useTheme();
  const { user } = useAuth();

  // Clean demo-ready navigation - Core flows only
  const navigationSections: NavigationSection[] = [
    {
      title: 'Main',
      items: [
        {
          path: '/dashboard',
          label: 'Dashboard',
          icon: <DashboardIcon />,
        },
        {
          path: '/hotels',
          label: 'Properties',
          icon: <HotelIcon />,
        },
        {
          path: '/add-hotel',
          label: 'Add Property',
          icon: <AddHotelIcon />,
        },
      ],
    },
    {
      title: 'Portfolio Analytics',
      items: [
        {
          path: '/portfolio',
          label: 'Analytics',
          icon: <AnalyticsIcon />,
          isNew: true,
        },
      ],
    },
    {
      title: 'Tools',
      items: [
        {
          path: '/ocr',
          label: 'Document Upload',
          icon: <OCRIcon />,
          isNew: true,
        },
        {
          path: '/chat',
          label: 'AI Assistant',
          icon: <ChatIcon />,
          badge: 'AI',
        },
      ],
    },
  ];

  // Add admin section if user is an admin
  if (user && (user as any).role === 'admin') {
    navigationSections.push({
      title: 'Administration',
      items: [
        {
          path: '/admin',
          label: 'User Management',
          icon: <AdminIcon />,
        },
      ],
    });
  }

  // Simplified function to render menu items (no submenus)
  const renderNavItem = (item: NavigationItem, index: number) => {
    return (
      <ListItem key={item.path + index} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: open ? 'initial' : 'center',
            px: 2.5,
            borderRadius: '8px',
            mb: 0.5,
            position: 'relative',
            '&:hover': {
              backgroundColor: alpha('#ffffff', 0.08),
              transform: 'translateX(2px)',
            },
            transition: 'all 0.2s ease',
          }}
          onClick={() => onNavigate(item.path)}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 2 : 'auto',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            {item.badge ? (
              <Badge
                badgeContent={item.badge}
                color={typeof item.badge === 'string' ? 'secondary' : 'error'}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    height: 16,
                    minWidth: 16,
                  }
                }}
              >
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>

          {open && (
            <ListItemText
              disableTypography
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'white' }}>
                    {item.label}
                  </Typography>
                  {item.isPro && (
                    <Typography
                      variant="caption"
                      sx={{
                        backgroundColor: theme.palette.warning.main,
                        color: 'white',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                      }}
                    >
                      PRO
                    </Typography>
                  )}
                </Box>
              }
              sx={{ opacity: open ? 1 : 0 }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <>
      {/* Enhanced Logo and app name */}
      {open && (
        <Logo>
          <img
            src="/logo192-dark.png"
            alt="Valuate Hotels"
            style={{ width: 32, height: 32, marginRight: 12, borderRadius: 6 }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'white',
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Valuate
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha('#ffffff', 0.7),
                fontSize: '0.7rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Hotel Analytics Platform
            </Typography>
          </Box>
        </Logo>
      )}

      {/* Navigation sections with headers */}
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flexGrow: 1,
          px: 0.5,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha('#ffffff', 0.1),
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#ffffff', 0.3),
            borderRadius: '2px',
            '&:hover': {
              background: alpha('#ffffff', 0.4),
            },
          },
        }}
      >
        {navigationSections.map((section, _sectionIndex) => (
          <Box key={section.title}>
            {open && <SectionHeader>{section.title}</SectionHeader>}
            <List sx={{ px: 0, py: 0 }}>
              {section.items.map(renderNavItem)}
            </List>
          </Box>
        ))}
      </Box>

      {/* Settings at the bottom */}
      {open && (
        <>
          <Divider sx={{ bgcolor: alpha('#ffffff', 0.12), my: 1 }} />
          <List sx={{ px: 1, py: 1 }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.08),
                  transform: 'translateX(2px)',
                },
                transition: 'all 0.2s ease',
              }}
              onClick={() => onNavigate('/settings')}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Settings & Preferences"
                sx={{
                  opacity: open ? 1 : 0,
                  '& .MuiTypography-root': {
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: 'white'
                  }
                }}
              />
            </ListItemButton>
          </List>
        </>
      )}
    </>
  );
};

export default SidebarNavigation;
